import React, {useEffect, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import Chip from '@mui/material/Chip';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import {useSelector} from 'react-redux';
import {user} from '../../store/reducers/user';
import moment from 'moment';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {Grid} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import AssignmentIcon from '@mui/icons-material/Assignment';
import config from '../../app/config';
import {useTheme} from '@mui/material/styles';
import {UtilsMomentJS} from '../../store/utilsMoment';
import {AutocompleteDictionary} from './autocomplete';
import CheckIcon from '@mui/icons-material/Check';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Formatter from '../../util/formatter';
import AgyleDialog from '../../components/modal/agyleDialog';
import useOpenState from '../../hooks/useOpenState';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import MessageBox from '../../util/messageBox';
import AgyleTextField from '../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import {agylePref} from '../../store/reducers/preferences';
import {diagnosisQualifier} from '../../store/reducers/dicts';
import AgyleDictionaryField from '../../components/ui/selector/agyleDictionaryField';
import {AgyleTable, createAgyleTableColumn} from '../../components/table/AgyleTable';
import {calculateArrivalDate} from '../../util/patientUtil';
import DiagnosisQualifierModal from '../../components/modal/diagnosisQualifierModal';
import {ctxSplitScreen} from '../../store/reducers/patientContext';

const useStyles = makeStyles((theme) => ({
    avatarCustom: {
        width: 'auto !important',
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: '#fff',
    },
    spanDescription: {
        color: '#fff',
        fontWeight: 'bold',
    },
    diagnosisSequenceNumber: {
        backgroundColor: 'transparent !important',
    },
    buttonDialog: {
        width: 25,
        marginLeft: 5,
    },
    input: {
        width: '100%',
        fontSize: '15px',
        backgroundColor: theme.palette.background.paper,
        margin: 10,
    },
    table: {
        width: 'auto',
        margin: '20px',
        padding: '10px',
    },
    headCell: {
        fontSize: 14,
        background: theme.palette.background.default,
    },
    rowCell: {
        fontSize: 'small',
        '&.odd': {
            background: '#666666',
        },
    },
    endAdornmentButton: {
        opacity: 0.8,
        color: theme.palette.primary.main,
    },
    error: {
        color: '#f44336',
    },
}));

const AdvancedAutocompleteDictionary = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const theme = useTheme();
    const tester = Formatter.formatUserFullName(userObject);
    const agylePreference = useSelector(agylePref);
    const splitScreen = useSelector(ctxSplitScreen);

    const firstUpdate = useRef(true);
    const [stopUpdate, setStopUpdate] = useState(false);

    // Autocomplete dict type
    const {type, ecdsVersion} = props;
    const isDiagnosis = type === 'Diagnosis';
    const isReqTreatInvestEnabled = Boolean(config?.requestedTreatmentAndInvestigation);
    let changeStatusButtons = [];

    // Diagnosis Qualifier Dictionary
    const dictQualifier = useSelector(diagnosisQualifier);
    const [suspected, setSuspected] = React.useState(null);
    const [confirmed, setConfirmed] = React.useState(null);
    const [isQualifierOpen, openQualifier, closeQualifier] = useOpenState(false, true);
    const idUsed = useRef(null);
    const newDiagnosis = useRef(false);

    // Value for Advance Mode
    const [orgVal, setOrgVal] = React.useState(null);
    const [value, setValue] = React.useState(props.multiple ? [] : null);
    const [update, setUpdate] = React.useState(props.update ? props.update : 1);
    const [selectedValue, setSelectedValue] = React.useState(null);
    const [pickersValues, setPickersValues] = React.useState({
        requestDate: null,
        procedureTimestamp: null,
    });
    const [buttons, setButtons] = React.useState([]);

    // Validation
    const [errorPicker, setErrorPicker] = React.useState(null);

    // Dialog Open State
    const [isInformationOpen, openInformation, closeInformation] = useOpenState(false, false);
    const [isAllInformationOpen, openAllInformation, closeAllInformation] = useOpenState(false, false);

    // Confirm delete states
    const [tempNewVal, setTempNewVal] = React.useState(null);
    const removeType = useRef(null);
    const [isConfirmRemoveElementOpen, openConfirmRemoveElement, closeConfirmRemoveElement] = useOpenState(false, true);

    // Checking if an old format is loaded
    useEffect(() => {
        let clone = [];
        if (props.value) {
            let cloneDeep = _.cloneDeep(props.value);
            cloneDeep = cloneDeep.filter(element => {
                return !element.__id;
            });
            _.forEach(cloneDeep, c => {
                if (!c.procedureTimestamp) {
                    let cloneObj = _.cloneDeep(c);
                    const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(cloneObj.procedureDate, cloneObj.procedureTime);
                    cloneObj['procedureTimestamp'] = dateUTC ? dateUTC.toISOString() : null;
                    clone.push(cloneObj);
                } else {
                    clone.push(c);
                }
            });
        }
        setOrgVal(clone);
        setValue(clone);
    }, []);

    useEffect(() => {
        if (!_.isEmpty(dictQualifier)) {
            setSuspected(_.find(dictQualifier, {ecdsVersion: ecdsVersion, snomedCode: '415684004'}));
            setConfirmed(_.find(dictQualifier, {ecdsVersion: ecdsVersion, snomedCode: '410605003'}));
        }
    }, [dictQualifier]);

    useEffect(() => {
        if (props.value && props.update !== update && !_.isEqual(props.value, value)) {
            const cloneDeepTmp = _.cloneDeep(props.value);
            let t = cloneDeepTmp.filter(element => {
                return !element.__id;
            });

            if (t !== value) {
                let clone = [];
                if (props.value) {
                    const cloneDeep = _.cloneDeep(props.value);
                    clone = cloneDeep.filter(element => {
                        return !element.__id;
                    });
                }
                setValue(clone);
                setUpdate(props.update);
            }
        } else if (props.update !== update) {
            setUpdate(props.update);
        }
    }, [props.update, update]);

    useEffect(() => {
        if (!firstUpdate.current && !errorPicker && !newDiagnosis.current && !stopUpdate) {
            if (!_.isEqual(value, orgVal)) {
                props.onChange(value);
                setOrgVal(value);
            }
        }
    }, [value, newDiagnosis.current, stopUpdate]);

    useEffect(() => {
        setErrorPicker(null);
    }, [isInformationOpen]);

    useEffect(() => {
        if (props?.deleteEntries) {
            let btns = [];
            btns.push(
                <AgyleButton
                    onClick={() => {
                        closeAllInformation();
                        handleChangeAdvancedMode([], 'clear');
                    }}
                    variant="contained"
                    color="primary"
                >
                    {t('Delete All Entries')}
                </AgyleButton>,
            );

            setButtons(btns);
        }
    }, [props]);

    const getLocalDate = (object) => {
        let time;
        if (object?.procedureTimestamp) {
            time = moment(object?.procedureTimestamp);
        } else {
            time = (_.isEmpty(object.procedureDate) || _.isEmpty(object.procedureTime))
                ? null
                : UtilsMomentJS.getLocalDateFromUTCECDSFormat(object.procedureDate, object.procedureTime);
        }

        setPickersValues({
            procedureTimestamp: time || null,
            requestDate: object?.requestedTimestamp || null,
        });
    };

    const renderIcon = (opt) => {
        if (isDiagnosis) {
            return (
                opt?.qualifier?.snomedCode === '415684004'
                    ? <QuestionMarkIcon
                        fontSize={props.size}
                        style={{color: '#FFF'}}
                    />
                    : <CheckIcon
                        fontSize={props.size}
                        style={{color: '#FFF'}}
                    />
            );
        } else if (isReqTreatInvestEnabled) {
            const inRequested = _.isEmpty(opt?.procedureDate) || _.isEmpty(opt?.procedureTime);
            return (
                !inRequested
                    ? <CheckIcon
                        fontSize={props.size}
                        style={{color: '#FFF'}}
                    />
                    : <WatchLaterIcon
                        fontSize={props.size}
                        style={{color: '#FFF'}}
                    />
            );
        }
    };

    const renderDescription = (opt) => {
        if (props.renderText) {
            return opt?.value[opt?.value?.diagnosisMax ? 'snomedFullySpecifiedName' : props.renderText];
        }
        return opt?.value?.__description;
    };

    const renderInput = (opt) => {
        return (
            <>
                {isDiagnosis && opt.sequenceNumber !== '' && (
                    <Chip
                        className={classes.diagnosisSequenceNumber}
                        style={{
                            marginRight: '-18px',
                            marginLeft: '-13px',
                        }}
                        color={isDiagnosis ? 'default' : props.color}
                        size={props.size}
                        avatar={
                            <div
                                className={classes.avatarCustom}
                                style={{
                                    lineHeight: props.size === 'small' ? '18px' : '24px',
                                    color: '#fff',
                                    background: checkQualifier(opt, 'avatar'),
                                }}
                            >
                                {opt.sequenceNumber}
                            </div>
                        }
                    />
                )}

                {(!props.withoutAvatar && agylePreference?.showSnomedCode) &&
                    <Chip
                        className={classes.diagnosisSequenceNumber}
                        style={{
                            marginRight: '-11px',
                            marginLeft: (!isDiagnosis || opt.sequenceNumber === '') ? '-13px' : '',
                        }}
                        color={isDiagnosis ? 'default' : props.color}
                        size={props.size}
                        avatar={
                            <div
                                className={classes.avatarCustom}
                                style={{
                                    lineHeight: props.size === 'small' ? '18px' : '24px',
                                    color: '#fff',
                                    background: checkQualifier(opt, 'avatar'),
                                }}
                            >
                                {opt?.value?.snomedCode ? opt?.value?.snomedCode : 'UNK'}
                            </div>
                        }
                    />
                }

                <span
                    className={classes.spanDescription}
                    style={{
                        lineHeight: props.size === 'small' ? '18px' : '24px',
                        marginLeft: (isDiagnosis && agylePreference?.showSnomedCode) ? 0 : 5,
                    }}
                >
                    {renderDescription(opt)}
                </span>

                {(isDiagnosis || isReqTreatInvestEnabled) &&
                    <IconButton
                        className={classes.buttonDialog}
                        size={props.size}
                        style={{
                            fontSize: props.size === 'small' ? 15 : 17,
                            lineHeight: props.size === 'small' ? '18px' : '24px',
                        }}
                        disableRipple
                        onClick={() => {
                            if (isDiagnosis) handleChangeQualifier(opt.id, opt?.qualifier?.snomedCode !== '415684004');
                            else if (isReqTreatInvestEnabled) handleChangeStatusTreatInv(opt.id);
                        }}
                    >
                        {renderIcon(opt)}
                    </IconButton>
                }

                <IconButton
                    className={classes.buttonDialog}
                    size={props.size}
                    style={{
                        fontSize: props.size === 'small' ? 15 : 17,
                        lineHeight: props.size === 'small' ? '18px' : '24px',
                    }}
                    disableRipple
                    onClick={() => {
                        getLocalDate(opt);
                        setSelectedValue(opt);
                        openInformation();
                    }}
                >
                    <NoteAddIcon
                        fontSize={props.size}
                        style={{color: '#FFF'}}
                    />
                </IconButton>
            </>
        );
    };

    const sort = (a, b) => {
        const aNumber = Number(a.sequenceNumber !== '' ? a.sequenceNumber : -1);
        const bNumber = Number(b.sequenceNumber !== '' ? b.sequenceNumber : -1);
        if (aNumber === bNumber) {
            return 0;
        } else if (aNumber < 0) {
            return 1;
        } else if (bNumber < 0) {
            return -1;
        } else {
            return aNumber < bNumber ? -1 : 1;
        }
    };

    const handleChangeQualifier = (id, isSuspected) => {
        firstUpdate.current = false;
        newDiagnosis.current = false;
        let cloneValue = _.cloneDeep(value);
        const diagIndex = _.findIndex(cloneValue, {id});
        if (diagIndex !== -1) {
            let newQualifier = isSuspected ? suspected : confirmed;
            if (cloneValue[diagIndex].qualifier.__id !== newQualifier.__id) {
                cloneValue[diagIndex] = {
                    ...cloneValue[diagIndex],
                    qualifier: newQualifier,
                };
                setValue(cloneValue);
            }
        }
    };

    const handleChangeStatusTreatInv = (id) => {
        const currId = (id || selectedValue?.id) || null;
        if (currId) {
            firstUpdate.current = false;
            newDiagnosis.current = false;
            let cloneValue = _.cloneDeep(value);
            const diagIndex = _.findIndex(cloneValue, {id: currId});
            if (diagIndex !== -1) {
                const currentUTC = moment().utc();
                const obj = cloneValue[diagIndex];
                const isCompleted = !_.isEmpty(obj?.procedureDate) && !_.isEmpty(obj?.procedureTime);

                cloneValue[diagIndex] = {
                    ...cloneValue[diagIndex],
                    procedureDate: isCompleted ? null : currentUTC.format(config.ecdsDateFormat),
                    procedureTime: isCompleted ? null : currentUTC.format(config.ecdsTimeFormat),
                    procedureTimestamp: isCompleted ? null : moment().toISOString(),
                };
                setValue(cloneValue);
                handleCloseInformation();
            }
        }
    };

    const handleCloseInformation = () => {
        setSelectedValue(null);
        setErrorPicker(null);
        closeInformation();
    };

    const handleChangeValue = (newValue, customId) => {
        const id = customId || uuidv4() + '';
        const currentUTC = moment().utc();
        let clone = newValue.slice().filter(element => !element.__id);
        let filterNewEntry = _.cloneDeep(newValue);
        filterNewEntry = filterNewEntry.filter(element => element.__id);

        if (filterNewEntry && filterNewEntry.length > 0) {
            let data = {
                id,
                value: filterNewEntry[0],
                date: moment().toISOString(),
                who: tester,
                procedureDate: currentUTC.format(config.ecdsDateFormat),
                procedureTime: currentUTC.format(config.ecdsTimeFormat),
                procedureTimestamp: moment().toISOString(),
            };
            if (isDiagnosis) {
                data = {
                    ...data,
                    qualifier: suspected || null,
                    sequenceNumber: clone.length + 1,
                };
            }
            if (isReqTreatInvestEnabled && !isDiagnosis) {
                data = {
                    ...data,
                    procedureDate: null,
                    procedureTime: null,
                    procedureTimestamp: null,
                    requestedTimestamp: moment().toISOString(),
                };
            }
            clone.push(data);
        }

        // After add a new object then assign a new value of sequence number
        if (isDiagnosis) {
            clone = _.cloneDeep(clone);
            clone.forEach((obj, index) => {
                obj.sequenceNumber = index + 1;
            });
        }

        firstUpdate.current = false;
        if (isDiagnosis) clone.sort((a, b) => sort(a, b));
        setValue(clone);
    };

    const handleChangeAdvancedMode = (newValue, reason) => {
        const id = uuidv4() + '';
        const currentUTC = moment().utc();
        if (!props.multiple) {
            let data = {
                id,
                value: newValue,
                date: moment().toISOString(),
                who: tester,
                procedureDate: currentUTC.format(config.ecdsDateFormat),
                procedureTime: currentUTC.format(config.ecdsTimeFormat),
                procedureTimestamp: moment().toISOString(),
            };

            if (isDiagnosis) {
                data = {
                    ...data,
                    qualifier: suspected || null,
                    sequenceNumber: '1',
                };
            }

            firstUpdate.current = false;
            setValue(data);
        } else {
            if (props.confirmDelete && (reason === 'removeOption' || reason === 'clear')) {
                setTempNewVal(newValue);
                openConfirmRemoveElement();
                removeType.current = reason;
            } else {
                handleChangeValue(newValue, id);
            }
        }

        if (isDiagnosis && reason === 'selectOption' && props.multiple) {
            idUsed.current = id;
            setStopUpdate(true);
            openQualifier();
        }
    };

    if (isReqTreatInvestEnabled && !isDiagnosis) {
        changeStatusButtons.push(
            <AgyleButton
                onClick={handleChangeStatusTreatInv.bind(this, null)}
                variant="contained"
            >
                {t('Change status')}
            </AgyleButton>,
        );
    }

    const validateDateTime = () => {
        let valid = true;
        if (_.isEmpty(pickersValues.procedureTimestamp)) {
            valid = false;
            setErrorPicker(true);
        }
        return valid;
    };

    const saveInfoData = () => {
        if (validateDateTime()) {
            firstUpdate.current = false;
            newDiagnosis.current = false;
            let clone = _.cloneDeep(value);
            let index = _.findIndex(clone, {'id': selectedValue.id});
            clone[index] = selectedValue;

            if (isDiagnosis) {
                clone.sort((a, b) => sort(a, b));
            }

            setValue(clone);
            closeInformation();
            setPickersValues({
                requestDate: null,
                procedureTimestamp: null,
            });
            setSelectedValue(null);
        }
    };

    const getLabelTime = () => {
        if (isDiagnosis) return t('Diagnosis');
        return t(type === 'Investigation' ? 'Investigation' : 'Procedure');
    };

    const renderEditInfoData = () => {
        const handleMaxValue = (event) => {
            const {value} = event.target;
            if (value.length <= 5 && Number(value) >= 0) {
                setSelectedValue(prevState => ({...prevState, sequenceNumber: value}));
            }
        };

        const getQualifier = () => _.filter(dictQualifier, {ecdsVersion});

        return (
            <Grid container spacing={2} direction="row" style={{paddingRight: 25}}>
                <Grid item xs={12}>
                    <AgyleTextField
                        label={type}
                        value={selectedValue?.value?.[selectedValue?.value?.diagnosisMax ? 'snomedFullySpecifiedName' : 'snomedDescription']}
                        disabled
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        label={t('Snomed Code')}
                        value={selectedValue?.value?.diagnosisMax ? selectedValue?.value?.usedParentSnomedCode : selectedValue?.value?.snomedCode}
                        disabled
                        fullWidth
                    />
                </Grid>

                {(!isDiagnosis && isReqTreatInvestEnabled) &&
                    <Grid item xs={splitScreen?.enabled ? 12 : 6}>
                        <AgyleDatePicker
                            label={t('Requested Date')}
                            type="dateTime"
                            value={pickersValues?.requestDate || null}
                            disableFuture
                            disabled
                            onChange={() => {
                            }}
                        />
                    </Grid>
                }

                <Grid item xs={isDiagnosis ? 12 : (splitScreen?.enabled ? 12 : 6)}>
                    <AgyleDatePicker
                        label={`${getLabelTime()} ${t('Date and Time')}`}
                        type="dateTime"
                        value={pickersValues.procedureTimestamp || null}
                        onChange={(event) => setPickersValues(prevState => ({
                            ...prevState,
                            procedureTimestamp: event,
                        }))}
                        disableFuture
                        minDate={moment(calculateArrivalDate(props.patient))}
                        onError={setErrorPicker}
                        error={errorPicker}
                    />
                </Grid>

                {isDiagnosis &&
                    <>
                        <Grid item xs={splitScreen?.enabled ? 12 : 6}>
                            <ControlDiagnosisQualifier
                                label={t('Diagnosis Qualifier')}
                                value={selectedValue.qualifier}
                                onChange={(_e, val) => setSelectedValue(prevState => ({
                                    ...prevState,
                                    qualifier: val,
                                }))}
                                fullWidth
                                dict={getQualifier()}
                                disablePortal
                            />
                        </Grid>

                        <Grid item xs={splitScreen?.enabled ? 12 : 6}>
                            <AgyleTextField
                                label={t('Sequence No')}
                                onChange={handleMaxValue}
                                type="number"
                                value={selectedValue.sequenceNumber + ''}
                                fullWidth
                            />
                        </Grid>
                    </>
                }
            </Grid>
        );
    };

    const editItem = (row) => {
        getLocalDate(row);
        setSelectedValue(row);
        openInformation();
    };

    const deleteItem = (row) => {
        let clone = _.cloneDeep(value);
        clone = clone.filter(el => el.id !== row.id);
        setTempNewVal(clone);
        removeType.current = 'removeOption';
        openConfirmRemoveElement();
    };

    const renderTable = () => {
        let columns = [
            createAgyleTableColumn('no', 'No', 1, null, 'count', '', 'medium', 'left', true, true, 30, null),
            createAgyleTableColumn('entry', 'Description', 2, 'value', 'advanceDescription', '', 'medium', 'left', true, true, 200, null),
            createAgyleTableColumn('code', 'Code', 2, 'value', 'advanceSnomedCode', '', 'medium', 'left', true, true, 100, null)];
        if ((isReqTreatInvestEnabled && !isDiagnosis)) {
            columns.push(
                createAgyleTableColumn('reqTimestamp', 'Requested Timestamp', 3, 'requestedTimestamp', 'date', '', 'medium', 'left', true, true, 150, null),
            );
        }

        if (isDiagnosis) {
            columns.push(
                createAgyleTableColumn('date', 'Diagnosis Date', 4, null, 'dateECDS', '', 'medium', 'left', true, true, 150, null, ['procedureDate', 'procedureTime', 'date']),
                createAgyleTableColumn('time', 'Diagnosis Time', 5, null, 'dateECDS', '', 'medium', 'left', true, true, 150, null, ['procedureDate', 'procedureTime', 'time']),
                createAgyleTableColumn('qualifier', 'Diagnosis Qualifier', 5, 'qualifier.__description', 'string', '', 'medium', 'left', true, true, 150, null),
                createAgyleTableColumn('seqNumb', 'Sequence Number', 5, 'sequenceNumber', 'string', '', 'medium', 'left', true, true, 150, null),
            );
        } else {
            columns.push(
                createAgyleTableColumn('date', type === 'Investigation' ? 'Investigation Date' : 'Procedure Date', 4, null, 'dateECDS', '', 'medium', 'left', true, true, 150, null, ['procedureDate', 'procedureTime', 'date']),
                createAgyleTableColumn('time', type === 'Investigation' ? 'Investigation Time' : 'Procedure Time', 5, null, 'dateECDS', '', 'medium', 'left', true, true, 150, null, ['procedureDate', 'procedureTime', 'time']),
            );

        }
        if (!props.disableEditTable) {
            columns.push(
                createAgyleTableColumn('action', 'Action', 6, null, 'action', '', 'medium', 'right', true, true, 50, null),
            );
        }

        return (
            <div style={{padding: 10}}>
                <AgyleTable
                    data={value}
                    columns={columns}
                    edRefresh
                    disableFilter
                    editItem={editItem}
                    deleteItem={deleteItem}
                />
            </div>
        );
    };

    const checkQualifier = (element, mode) => {
        if (isReqTreatInvestEnabled) {
            // For diagnosis check qualifier for rest check procedure date
            if (isDiagnosis) {
                const qualifierSuspected = element?.qualifier && element?.qualifier?.snomedCode === '415684004';
                if (mode === 'chip') {
                    return qualifierSuspected ? (config?.diagnosisColorsSuspected?.chip || '#f7a202') : (config?.diagnosisColorsConfirm?.chip || '#4eac6f');
                } else {
                    return qualifierSuspected ? (config?.diagnosisColorsSuspected?.main || '#634101') : (config?.diagnosisColorsConfirm?.main || '#2e6441');
                }
            } else {
                const requested = _.isEmpty(element?.procedureDate) || _.isEmpty(element?.procedureTime);
                if (mode === 'chip') {
                    return requested ? (config?.treatmentInvColorsRequested?.chip || '#e25b61') : (config?.treatmentInvColors?.chip || '#4eac6f');
                } else {
                    return requested ? (config?.treatmentInvColorsRequested?.main || '#70191c') : (config?.treatmentInvColors?.main || '#2e6441');
                }
            }
        } else if (isDiagnosis) {
            const qualifierSuspected = element?.qualifier && element?.qualifier?.snomedCode === '415684004';
            const check = element.qualifier === null || element.sequenceNumber === '';
            if (check) return '';

            if (mode === 'chip') {
                return qualifierSuspected ? theme.palette.primary.dark : theme.palette.primary.main;
            } else {
                return qualifierSuspected ? theme.palette.primary.main : theme.palette.primary.dark;
            }
        }
        return '';
    };

    const renderRendAdornment = () => {
        return (
            props.multiple && value && !_.isEmpty(value)) && (
            <IconButton
                size={props.size}
                className={classes.endAdornmentButton}
                onClick={openAllInformation}
            >
                <AssignmentIcon fontSize={props.size}/>
            </IconButton>
        );
    };

    const renderCustomTags = (tagValue, getTagProps) => {
        return (
            tagValue.map((option, index) => (
                <Chip
                    style={{background: checkQualifier(option, 'chip'), marginRight: '10px'}}
                    color={isDiagnosis || isReqTreatInvestEnabled ? 'default' : 'primary'}
                    size={props.size}
                    label={renderInput(option)}
                    {...getTagProps({index})}
                />
            ))
        );
    };

    const handleCloseConfirmRemove = (e) => {
        closeConfirmRemoveElement(e);
        setTempNewVal(null);
        removeType.current = null;
    };

    const handleRemove = () => {
        closeConfirmRemoveElement();
        handleChangeValue(tempNewVal);
        setTempNewVal(null);
        removeType.current = null;
    };

    const handleCancelDiagnosis = (id) => {
        setValue(_.filter(value, el => el.id !== id));
        setStopUpdate(false);
        closeQualifier();
    };

    const handleSubmitQualifier = (diagId, diagnosis, diagnosisMax, qualifier) => {
        let clone = _.cloneDeep(value);
        let diag = clone.find(d => d.id === diagId);
        if (diag) {
            diag['qualifier'] = qualifier;
            if (diagnosisMax) {
                diag['value'] = diagnosisMax;
                diag['value']['usedParentSnomedCode'] = diagnosis?.value?.snomedCode;
            }
        }
        setValue(clone);
        setStopUpdate(false);
        closeQualifier();
    };

    return (
        <>
            <AutocompleteDictionary
                dontTrimSearch={_.isUndefined(props.dontTrimSearch) ? true : props.dontTrimSearch}
                isOptionEqualToValue={(option, value) => option.snomedCode === (value?.value?.diagnosisMax ? value?.value?.usedParentSnomedCode : value?.value?.snomedCode)}
                value={value}
                onChangeAdvanced={(newValue, reason) => handleChangeAdvancedMode(newValue, reason)}
                endAdornmentAddons={renderRendAdornment}
                renderTags={renderCustomTags}
                {...props}
            />

            {/* Edit Additional Info */}
            <AgyleDialog
                title={t('Additional Information')}
                open={isInformationOpen}
                width="md"
                fullWidth
                onClose={handleCloseInformation}
                onSave={saveInfoData}
                disableSubmit={!_.isNil(errorPicker)}
                buttons={changeStatusButtons}
                zIndex={1400}
            >
                <>
                    {selectedValue !== null && renderEditInfoData()}
                </>
            </AgyleDialog>

            {/* List of All Entries */}
            <AgyleDialog
                title={t('All Entry Information')}
                open={isAllInformationOpen}
                onClose={closeAllInformation}
                width="lg"
                noPadding
                fullWidth
                disableSave
                closeButtonOnHeader
                buttons={buttons}
            >
                <>
                    {renderTable()}
                </>
            </AgyleDialog>

            {isDiagnosis && isQualifierOpen &&
                <DiagnosisQualifierModal
                    open={isQualifierOpen}
                    diagnosis={value}
                    diagId={idUsed.current}
                    ecdsVersion={ecdsVersion}
                    cancelDiagnosis={handleCancelDiagnosis}
                    onSubmit={handleSubmitQualifier}
                />
            }

            {isConfirmRemoveElementOpen &&
                <MessageBox
                    onClose={handleCloseConfirmRemove}
                    title={t('Confirmation')}
                    haveAccept={{yes: t('Yes'), no: t('No')}}
                    onAccept={handleRemove}
                    message={t(
                        removeType.current === 'removeOption'
                            ? 'Are you sure you want to remove this entry?'
                            : 'Are you sure you want to clear all values?',
                    )}
                    open={isConfirmRemoveElementOpen}
                    warning
                />
            }
        </>
    );
};

// Single choice Diagnosis Qualifier
function ControlDiagnosisQualifier(props) {
    function filterOptions(options, {inputValue}, opt = ['__description']) {
        return options.filter((option) => opt.some((field) => option[field]?.toLowerCase()?.includes(inputValue.toLowerCase())));
    }

    return <AgyleDictionaryField
        filterOptions={filterOptions}
        options={props.dict}
        {...props}
    />;
}

export default AdvancedAutocompleteDictionary;
